import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NextLink,
  Operation,
  from,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { omitDeep } from 'deepdash/standalone';

import { RUMHelpers } from 'services/observability/datadog';
import { getXsrfToken } from 'utils/cookies';

export enum OperationType {
  QUERY = 'query',
  MUTATION = 'mutation',
  SUBSCRIPTION = 'subscription',
}

const OPERATION_DEFINITION = 'OperationDefinition';

export const removeTypenameFromMutation = (operation: Operation, forward: NextLink) => {
  const mutationDefinition = operation?.query?.definitions?.filter(
    (def) => def.kind === OPERATION_DEFINITION && def.operation === OperationType.MUTATION,
  )?.[0];

  if (mutationDefinition) {
    /* eslint-disable-next-line no-param-reassign */
    operation.variables = omitDeep(operation.variables, '__typename');
  }

  return forward(operation);
};

const errorLink = onError((args) => {
  const { graphQLErrors = [], networkError, operation } = args;
  const message = graphQLErrors.map((e) => e.message).join('\n');
  const err = new Error(message);
  err.name = 'GraphQLError';

  /**
   * Additional error information added to the error log in datadog.
   * You can find this information by checking the error attributes.
   */
  const info = {
    graphQLErrors,
    operationName: operation?.operationName,
    query: {
      body: operation?.query?.loc?.source?.body,
    },
    networkError,
  };

  RUMHelpers.addRUMError(err, 'GRAPHQL_ERROR', info);
});

const removeTypenameFromMutationLink = new ApolloLink(removeTypenameFromMutation);

const httpLink = new HttpLink({
  uri: '/graphql',
  headers: {
    'X-XSRF-TOKEN': getXsrfToken(),
  },
});

const mockLink = new HttpLink({
  uri: 'https://1cf09f66-f25d-4774-8321-17746f666028.mock.pstmn.io/mock',
  headers: {
    'X-XSRF-TOKEN': getXsrfToken(),
    'x-mock-match-request-body': true,
  },
});

const selectedLink = ApolloLink.split(
  (operation) => operation.getContext().clientName === 'mock', // Routes the query to the proper client
  mockLink,
  httpLink,
);

const client = new ApolloClient({
  cache: new InMemoryCache(),
  link: from([removeTypenameFromMutationLink, errorLink, selectedLink]),
  queryDeduplication: false,
  defaultOptions: {
    query: {
      // aggressive HTTP caching means when we refetch to sync up after a mutation we get stale data, this disables that
      fetchPolicy: 'no-cache',
    },
  },
});

export default client;
